import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */

export const _frontmatter = {
  "pageTitle": "Button API",
  "title": "Button",
  "props": ["ButtonProps"]
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const PageContent = makeShortcode("PageContent");
const LeadParagraph = makeShortcode("LeadParagraph");
const Link = makeShortcode("Link");
const ButtonProps = makeShortcode("ButtonProps");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <PageContent componentName="button" type="api" mdxType="PageContent">
      <LeadParagraph mdxType="LeadParagraph">
  Buttons are clickable items used to perform an action. See the{' '}
  <Link to="/design/button/" mdxType="Link">Button Design Guidelines</Link> for more
  information on when to use the different kinds of buttons.
      </LeadParagraph>
      <h2>{`Basic Usage`}</h2>
      <p>{`When using multiple buttons, they should be wrapped in a `}<Link to="/api/buttongroup" mdxType="Link">{`ButtonGroup`}</Link>{`.`}</p>
      <p>{`When used with icons, the Button component takes a slightly different set of props. For information on how to use this component with icons, please see documentation on `}<Link to="/api/icon-button" mdxType="Link">{`Icon Buttons`}</Link>{`.`}</p>
      <h2>{`Sizes`}</h2>
      <p>{`Sizes for buttons include `}<inlineCode parentName="p">{`small`}</inlineCode>{`, `}<inlineCode parentName="p">{`medium`}</inlineCode>{`, and `}<inlineCode parentName="p">{`large`}</inlineCode>{`, with `}<inlineCode parentName="p">{`medium`}</inlineCode>{` being the default value.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Button, ButtonSize, ButtonGroup } from 'react-magma-dom';

export function Example() {
  return (
    <ButtonGroup>
      <Button size={ButtonSize.small}>Small</Button>
      <Button>Default</Button>
      <Button size={ButtonSize.large}>Large</Button>
    </ButtonGroup>
  );
}
`}</code></pre>
      <h2>{`Colors`}</h2>
      <p>{`Colors for buttons include `}<inlineCode parentName="p">{`primary`}</inlineCode>{`, `}<inlineCode parentName="p">{`secondary`}</inlineCode>{`, `}<inlineCode parentName="p">{`subtle`}</inlineCode>{`, `}<inlineCode parentName="p">{`danger`}</inlineCode>{`, `}<inlineCode parentName="p">{`success`}</inlineCode>{`, and `}<inlineCode parentName="p">{`marketing`}</inlineCode>{` with `}<inlineCode parentName="p">{`primary`}</inlineCode>{` being the default value.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Button, ButtonColor, ButtonGroup } from 'react-magma-dom';

export function Example() {
  return (
    <ButtonGroup>
      <Button color={ButtonColor.primary}>Primary (default)</Button>
      <Button color={ButtonColor.secondary}>Secondary</Button>
      <Button color={ButtonColor.subtle}>Subtle</Button>
      <Button color={ButtonColor.danger}>Danger</Button>
      <Button color={ButtonColor.success}>Success</Button>
      <Button color={ButtonColor.marketing}>Marketing</Button>
    </ButtonGroup>
  );
}
`}</code></pre>
      <h2>{`Variants`}</h2>
      <p>{`Variants for buttons include `}<inlineCode parentName="p">{`solid`}</inlineCode>{` and `}<inlineCode parentName="p">{`link`}</inlineCode>{`, with `}<inlineCode parentName="p">{`solid`}</inlineCode>{` being the default value.`}</p>
      <p>{`The `}<inlineCode parentName="p">{`marketing`}</inlineCode>{` buttons can only use the `}<inlineCode parentName="p">{`solid`}</inlineCode>{` variant.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  Button,
  ButtonColor,
  ButtonVariant,
  ButtonGroup,
} from 'react-magma-dom';

export function Example() {
  return (
    <>
      <ButtonGroup>
        <Button color={ButtonColor.primary} variant={ButtonVariant.solid}>
          Solid (default)
        </Button>
        <Button color={ButtonColor.primary} variant={ButtonVariant.link}>
          Link
        </Button>
      </ButtonGroup>
      <br />
      <ButtonGroup>
        <Button color={ButtonColor.secondary} variant={ButtonVariant.solid}>
          Solid (default)
        </Button>
      </ButtonGroup>
      <br />
      <ButtonGroup>
        <Button color={ButtonColor.danger} variant={ButtonVariant.solid}>
          Solid (default)
        </Button>
        <Button color={ButtonColor.danger} variant={ButtonVariant.link}>
          Link
        </Button>
      </ButtonGroup>
    </>
  );
}
`}</code></pre>
      <h2>{`Shapes`}</h2>
      <p>{`Shapes for buttons include `}<inlineCode parentName="p">{`fill`}</inlineCode>{`, `}<inlineCode parentName="p">{`leftCap`}</inlineCode>{`, and `}<inlineCode parentName="p">{`rightCap`}</inlineCode>{`, with `}<inlineCode parentName="p">{`fill`}</inlineCode>{` being the default value.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Button, ButtonColor, ButtonShape, ButtonGroup } from 'react-magma-dom';

export function Example() {
  return (
    <ButtonGroup>
      <Button color={ButtonColor.primary}>Fill (default)</Button>
      <Button color={ButtonColor.primary} shape={ButtonShape.leftCap}>
        Left Cap
      </Button>
      <Button color={ButtonColor.primary} shape={ButtonShape.rightCap}>
        Right Cap
      </Button>
    </ButtonGroup>
  );
}
`}</code></pre>
      <h2>{`Loading`}</h2>
      <p>{`When a button is in a loading state, the label can be replaced with a loading spinner using the `}<inlineCode parentName="p">{`isLoading`}</inlineCode>{` prop.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Button, ButtonGroup, VisuallyHidden } from 'react-magma-dom';

export function Example() {
  const [isLoading, setIsLoading] = React.useState(false);
  React.useEffect(() => {
    if (isLoading) {
      setTimeout(() => {
        setIsLoading(false);
      }, 4000);
    }
  }, [isLoading]);

  return (
    <>
      <p>Click the button below to show the loading state</p>
      <VisuallyHidden>
        <span role="status">{isLoading ? 'Loading...' : 'Ready'}</span>
      </VisuallyHidden>
      <ButtonGroup>
        <Button isLoading={isLoading} onClick={() => setIsLoading(true)}>
          Save
        </Button>
      </ButtonGroup>
    </>
  );
}
`}</code></pre>
      <h2>{`Inverse`}</h2>
      <p>{`The `}<inlineCode parentName="p">{`isInverse`}</inlineCode>{` property is an optional boolean, that reverses the colors so that the buttons can better appear on a dark background.
The default value is `}<inlineCode parentName="p">{`false`}</inlineCode>{`.`}</p>
      <p>{`While `}<inlineCode parentName="p">{`link isInverse buttons`}</inlineCode>{` still accepts the `}<inlineCode parentName="p">{`color`}</inlineCode>{` property, the `}<inlineCode parentName="p">{`color`}</inlineCode>{` property only affects `}<inlineCode parentName="p">{`solid isInverse buttons`}</inlineCode>{`.`}</p>
      <p>{`The `}<inlineCode parentName="p">{`marketing`}</inlineCode>{` button does not have an inverse state, as it already is styled to appear well against a dark background.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  Button,
  ButtonColor,
  ButtonVariant,
  ButtonGroup,
  Card,
  CardBody,
  magma,
} from 'react-magma-dom';

export function Example() {
  return (
    <Card isInverse>
      <CardBody>
        <ButtonGroup>
          <Button isInverse>Solid inverse</Button>
          <Button color={ButtonColor.secondary} isInverse>
            Solid secondary inverse
          </Button>
          <Button color={ButtonColor.subtle} isInverse>
            Solid subtle inverse
          </Button>
          <Button color={ButtonColor.danger} isInverse>
            Solid danger inverse
          </Button>
          <Button color={ButtonColor.success} isInverse>
            Solid success inverse
          </Button>
          <Button color={ButtonColor.marketing} isInverse>
            Solid marketing inverse
          </Button>
        </ButtonGroup>
        <ButtonGroup style={{ margin: magma.spaceScale.spacing03 }}>
          <Button variant={ButtonVariant.link} isInverse>
            Link inverse
          </Button>
        </ButtonGroup>
        <ButtonGroup>
          <Button disabled isInverse>
            Disabled inverse
          </Button>
          <Button disabled isInverse variant={ButtonVariant.link}>
            Disabled link inverse
          </Button>
        </ButtonGroup>
      </CardBody>
    </Card>
  );
}
`}</code></pre>
      <h2>{`Text Transform`}</h2>
      <p>{`Options for the `}<inlineCode parentName="p">{`textTransform`}</inlineCode>{` prop for buttons include `}<inlineCode parentName="p">{`uppercase`}</inlineCode>{` and `}<inlineCode parentName="p">{`none`}</inlineCode>{`, with `}<inlineCode parentName="p">{`uppercase`}</inlineCode>{` (all caps) being the default value.
This sets the CSS `}<inlineCode parentName="p">{`text-transform`}</inlineCode>{` property.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Button, ButtonTextTransform, ButtonGroup } from 'react-magma-dom';

export function Example() {
  return (
    <ButtonGroup>
      <Button>All caps (default)</Button>
      <Button textTransform={ButtonTextTransform.none}>
        No Text Transform
      </Button>
      <Button textTransform={ButtonTextTransform.uppercase}>
        Uppercase transform
      </Button>
    </ButtonGroup>
  );
}
`}</code></pre>
      <h2>{`Full Width`}</h2>
      <p>{`Passing the `}<inlineCode parentName="p">{`isFullWidth`}</inlineCode>{` property will set the button to display full-width.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Button, ButtonColor } from 'react-magma-dom';

export function Example() {
  return (
    <>
      <Button isFullWidth>Full Width button</Button>
      <br />
      <Button isFullWidth color={ButtonColor.secondary}>
        Full Width button
      </Button>
    </>
  );
}
`}</code></pre>
      <h2>{`Disabled`}</h2>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Button, ButtonVariant, ButtonGroup } from 'react-magma-dom';

export function Example() {
  return (
    <ButtonGroup>
      <Button disabled>Disabled (default)</Button>
      <Button disabled variant={ButtonVariant.solid}>
        Disabled Solid
      </Button>
      <Button disabled variant={ButtonVariant.link}>
        Disabled Link
      </Button>
    </ButtonGroup>
  );
}
`}</code></pre>
      <h2>{`Types`}</h2>
      <p>{`The button types `}<inlineCode parentName="p">{`submit`}</inlineCode>{` and `}<inlineCode parentName="p">{`reset`}</inlineCode>{` are able to be passed in to change the buttons behavior in forms.
By default, buttons will have a type of `}<inlineCode parentName="p">{`button`}</inlineCode>{`.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  Button,
  ButtonType,
  ButtonGroup,
  Input,
  Spacer,
} from 'react-magma-dom';

export function Example() {
  const inputRef = React.useRef<HTMLInputElement>();

  function onFormSubmit(event: React.FormEventHandler) {
    event.preventDefault();
    alert('Form Submitted');
  }

  function onFormReset(event: React.FormEventHandler) {
    event.preventDefault();
    inputRef.current.value = '';
  }

  return (
    <form onSubmit={onFormSubmit} onReset={onFormReset}>
      <Input ref={inputRef} labelText="Form Field" value="" />
      <Spacer size="12" />
      <ButtonGroup>
        <Button type={ButtonType.submit}>Submit</Button>
        <Button type={ButtonType.reset}>Reset</Button>
      </ButtonGroup>
    </form>
  );
}
`}</code></pre>
      <h2>{`Event Handling`}</h2>
      <p>{`The `}<inlineCode parentName="p">{`onClick`}</inlineCode>{` event will fire when the button is clicked, or when the enter or space bar key is pressed.
Events do not fire on disabled buttons.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Button, ButtonGroup } from 'react-magma-dom';

export function Example() {
  const [counter, setCounter] = React.useState<number>(0);

  function handleUpdateCounter() {
    setCounter(count => count + 1);
  }

  return (
    <>
      <p>
        <strong>Counter: </strong> <span id="counter">{counter}</span>
      </p>
      <ButtonGroup>
        <Button onClick={handleUpdateCounter}>Click me</Button>
        <Button disabled onClick={handleUpdateCounter}>
          Click me
        </Button>
      </ButtonGroup>
    </>
  );
}
`}</code></pre>
      <h2>{`Forward Ref`}</h2>
      <p>{`Using React's `}<inlineCode parentName="p">{`forwardRef`}</inlineCode>{` feature you can gain access to the reference of the internal button.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Button, ButtonGroup } from 'react-magma-dom';

export function Example() {
  const buttonRef = React.useRef<HTMLButtonElement>();

  function handleButtonFocus() {
    buttonRef.current.focus();
  }

  return (
    <ButtonGroup>
      <Button ref={buttonRef}>Button to be focused</Button>
      <Button onClick={handleButtonFocus}>Click to focus other button</Button>
    </ButtonGroup>
  );
}
`}</code></pre>
      <h2>{`Testing`}</h2>
      <p>{`Passing in the `}<inlineCode parentName="p">{`testId`}</inlineCode>{` prop will add the `}<inlineCode parentName="p">{`data-testid`}</inlineCode>{` attribute to the button element for easier querying in tests.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-html",
          "metastring": "noCodeSandbox",
          "noCodeSandbox": true
        }}>{`<button testId="test-id">Default</button>
`}</code></pre>
      <h2>{`Button Props`}</h2>
      <p><strong parentName="p">{`This component uses `}<inlineCode parentName="strong">{`forwardRef`}</inlineCode>{`. The ref is applied to the `}<inlineCode parentName="strong">{`button`}</inlineCode>{` element.`}</strong></p>
      <p>{`All of the `}<a parentName="p" {...{
          "href": "https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button"
        }}>{`standard button attributes`}</a>{` can be provided as props and will be applied to the `}<inlineCode parentName="p">{`button`}</inlineCode>{` element that gets rendered.`}</p>
      <ButtonProps mdxType="ButtonProps" />
    </PageContent>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      